import { QueryHookOptions } from "@apollo/client";
import { FILE_SHARE_MODE_NONE } from "core/consts";
import { FileHISStateful, FilesHisParams, QueryProgress } from "core/types";
import { useImportFilesHIS } from "onprem/hooks";
import { useEffect, useState } from "react";
import { useLoggedCareseekerConfig } from "reduxentities/hooks";
import { validateFileHIS } from "./utils";

type FilteredFilesHIS = {
  availableFiles: FileHISStateful[] | null;
  unavailableFiles: FileHISStateful[] | null;
};

export function useGetFilteredFilesHIS({
  externalId,
  skip,
}: FilesHisParams & Pick<QueryHookOptions, "skip">): [
  QueryProgress,
  FilteredFilesHIS,
] {
  const activateOnPrem = useLoggedCareseekerConfig(
    "on_premise_archive_integration_enabled",
  );
  const [filteredFilesHIS, setFilteredFilesHIS] = useState<FilteredFilesHIS>({
    availableFiles: null,
    unavailableFiles: null,
  });
  const { fetchFiles, files, queryProgress } = useImportFilesHIS({
    externalId,
    fromArchive: true,
  });

  useEffect(() => {
    if (activateOnPrem && !skip && externalId) {
      fetchFiles();
    }
  }, [activateOnPrem, skip, externalId]);

  useEffect(() => {
    if (files && Array.isArray(files)) {
      const result = files.reduce<FilteredFilesHIS>(
        (acc, fileHIS) => {
          const { isValid, type, unavailable_reason } = validateFileHIS({
            fileHIS,
          });

          if (isValid) {
            return {
              ...acc,
              availableFiles: [
                ...(acc.availableFiles || []),
                {
                  ...fileHIS,
                  id: fileHIS.document_id,
                  share_mode: FILE_SHARE_MODE_NONE,
                  type,
                  unavailable_reason: null,
                },
              ],
            };
          }

          return {
            ...acc,
            unavailableFiles: [
              ...(acc.unavailableFiles || []),
              {
                ...fileHIS,
                id: fileHIS.document_id,
                share_mode: FILE_SHARE_MODE_NONE,
                type: null,
                unavailable_reason,
              },
            ],
          };
        },
        {
          availableFiles: [],
          unavailableFiles: [],
        },
      );

      setFilteredFilesHIS(result);
    }
  }, [files]);

  return [queryProgress, filteredFilesHIS];
}
